import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { QrCode, Shield, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// QR Code Display Component
export const EmployeeQRCode = ({ employeeId, size = 180, showDetails = true }) => {
  const { getAuthHeaders, currentOffice } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchQRCode = async () => {
      if (!employeeId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const headers = getAuthHeaders();
        if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
        
        const endpoint = employeeId === 'me' 
          ? `${API_URL}/api/employees/me/qr-code`
          : `${API_URL}/api/employees/${employeeId}/qr-code`;
        
        const response = await fetch(endpoint, { headers });
        
        if (response.ok) {
          const data = await response.json();
          setQrData(data);
        } else {
          const err = await response.json();
          setError(err.detail || 'Failed to generate QR code');
        }
      } catch (err) {
        console.error('Error fetching QR code:', err);
        setError('Failed to generate QR code');
      } finally {
        setLoading(false);
      }
    };

    fetchQRCode();
  }, [employeeId]);

  const downloadQRCode = () => {
    if (!qrData?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = qrData.qr_code;
    link.download = `employee-qr-${qrData.employee_id || 'code'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <div className="animate-pulse bg-gray-200 rounded-lg" style={{ width: size, height: size }} />
        <p className="text-sm text-gray-500 mt-2">Generating QR code...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-4 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!qrData) return null;

  return (
    <div className="flex flex-col items-center">
      {/* QR Code Image */}
      <div className="bg-white p-3 rounded-xl shadow-sm border">
        <img 
          src={qrData.qr_code} 
          alt="Employee QR Code" 
          style={{ width: size, height: size }}
          className="rounded-lg"
        />
      </div>
      
      {showDetails && (
        <div className="mt-3 text-center">
          <p className="font-semibold text-gray-900">{qrData.employee_name}</p>
          <p className="text-sm text-gray-600">{qrData.job_title}</p>
          <p className="text-xs text-gray-500">{qrData.company}</p>
          <Badge className="mt-2 bg-green-100 text-green-800">
            <Shield className="h-3 w-3 mr-1" />
            Verified ID: {qrData.employee_id}
          </Badge>
        </div>
      )}
      
      <Button 
        variant="outline" 
        size="sm" 
        className="mt-3 gap-1"
        onClick={downloadQRCode}
      >
        <Download className="h-4 w-4" />
        Download
      </Button>
    </div>
  );
};

// QR Code Modal Component
export const EmployeeQRModal = ({ open, onClose, employeeId, employeeName }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-[#0F2E6E]" />
            Employee ID Card
          </DialogTitle>
          <DialogDescription>
            Scan this QR code to verify employee identity
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex justify-center py-4">
          <EmployeeQRCode employeeId={employeeId} size={200} showDetails={true} />
        </div>
        
        <div className="text-center text-xs text-gray-500 border-t pt-3">
          <p>This QR code contains encrypted employee information.</p>
          <p>Scanning will verify: Name, ID, Job Title, Company</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// QR Scanner/Verification Component
export const QRVerifier = () => {
  const [encryptedData, setEncryptedData] = useState('');
  const [result, setResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const verifyQRCode = async () => {
    if (!encryptedData.trim()) {
      toast.error('Please enter QR code data');
      return;
    }

    setVerifying(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/employees/qr-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encrypted_data: encryptedData }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to verify QR code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardContent className="p-6">
        <div className="text-center mb-4">
          <QrCode className="h-12 w-12 text-[#0F2E6E] mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Verify Employee QR Code</h3>
          <p className="text-sm text-gray-500">Paste the encrypted QR data to verify</p>
        </div>

        <textarea
          className="w-full p-3 border rounded-lg text-sm font-mono"
          rows={4}
          placeholder="Paste encrypted QR code data here..."
          value={encryptedData}
          onChange={(e) => setEncryptedData(e.target.value)}
        />

        <Button 
          className="w-full mt-3 bg-[#0F2E6E] hover:bg-[#0a1f4d]"
          onClick={verifyQRCode}
          disabled={verifying}
        >
          {verifying ? 'Verifying...' : 'Verify'}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-800">Verified Successfully</span>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Name:</span> {result.employee?.name}</p>
              <p><span className="font-medium">Employee ID:</span> {result.employee?.employee_id}</p>
              <p><span className="font-medium">Job Title:</span> {result.employee?.job_title}</p>
              <p><span className="font-medium">Company:</span> {result.employee?.company}</p>
              <p className="text-green-700 font-medium mt-2">
                ✓ {result.employee?.message}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeQRCode;
